import type { TrackPoint } from "./offline-draft";
import type { NavigationStats } from "./navigation-core";

export type ShareCardFormat = "story" | "portrait" | "square";
export type ShareCardBackground = "thera" | "map" | "photo" | "transparent";

export type ShareCardInput = {
  routeName: string;
  track: TrackPoint[];
  stats: NavigationStats;
  format: ShareCardFormat;
  background: ShareCardBackground;
  backgroundImage?: string | null;
  date?: Date;
};

const dimensions: Record<ShareCardFormat, [number, number]> = {
  story: [1080, 1920],
  portrait: [1080, 1350],
  square: [1080, 1080],
};

function roundedRectangle(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function loadImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("No fue posible abrir el fondo."));
    image.src = source;
  });
}

function drawCover(context: CanvasRenderingContext2D, image: HTMLImageElement, width: number, height: number) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const imageWidth = image.naturalWidth * scale;
  const imageHeight = image.naturalHeight * scale;
  context.drawImage(image, (width - imageWidth) / 2, (height - imageHeight) / 2, imageWidth, imageHeight);
}

function drawRoute(context: CanvasRenderingContext2D, track: TrackPoint[], x: number, y: number, width: number, height: number) {
  if (track.length < 2) return;
  const longitudes = track.map((point) => point.longitude);
  const latitudes = track.map((point) => point.latitude);
  const minimumLongitude = Math.min(...longitudes);
  const maximumLongitude = Math.max(...longitudes);
  const minimumLatitude = Math.min(...latitudes);
  const maximumLatitude = Math.max(...latitudes);
  const longitudeRange = Math.max(0.000001, maximumLongitude - minimumLongitude);
  const latitudeRange = Math.max(0.000001, maximumLatitude - minimumLatitude);
  const scale = Math.min(width / longitudeRange, height / latitudeRange);
  const routeWidth = longitudeRange * scale;
  const routeHeight = latitudeRange * scale;
  const left = x + (width - routeWidth) / 2;
  const top = y + (height - routeHeight) / 2;
  const step = Math.max(1, Math.ceil(track.length / 1500));

  context.beginPath();
  track.forEach((point, index) => {
    if (index % step !== 0 && index !== track.length - 1) return;
    const px = left + (point.longitude - minimumLongitude) * scale;
    const py = top + routeHeight - (point.latitude - minimumLatitude) * scale;
    if (index === 0) context.moveTo(px, py);
    else context.lineTo(px, py);
  });
  context.strokeStyle = "rgba(16, 35, 54, 0.42)";
  context.lineWidth = 26;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.stroke();
  context.strokeStyle = "#ff7f00";
  context.lineWidth = 14;
  context.stroke();
}

function duration(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
}

export async function createActivityShareCard(input: ShareCardInput) {
  const [width, height] = dimensions[input.format];
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Este navegador no permite crear imágenes.");

  if (input.background === "transparent") {
    context.clearRect(0, 0, width, height);
  } else if ((input.background === "map" || input.background === "photo") && input.backgroundImage) {
    const image = await loadImage(input.backgroundImage);
    drawCover(context, image, width, height);
    const gradient = context.createLinearGradient(0, height * 0.2, 0, height);
    gradient.addColorStop(0, "rgba(10, 28, 44, 0.08)");
    gradient.addColorStop(1, "rgba(10, 28, 44, 0.88)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  } else {
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#102a42");
    gradient.addColorStop(0.62, "#1b3148");
    gradient.addColorStop(1, "#315446");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    context.fillStyle = "rgba(255, 127, 0, 0.18)";
    context.beginPath();
    context.arc(width * 0.88, height * 0.1, width * 0.3, 0, Math.PI * 2);
    context.fill();
  }

  const padding = 70;
  const panelTop = height * 0.57;
  if (input.background === "transparent") {
    roundedRectangle(context, 35, 35, width - 70, height - 70, 42);
    context.fillStyle = "rgba(16, 42, 66, 0.92)";
    context.fill();
  }

  context.fillStyle = "#ffffff";
  context.font = "900 72px Arial";
  context.fillText("THERA", padding, 125);
  context.fillStyle = "#ff7f00";
  context.fillRect(padding, 150, 160, 9);
  context.fillStyle = "#ffffff";
  context.font = "700 44px Arial";
  const routeTitle = input.routeName.length > 34 ? `${input.routeName.slice(0, 33)}…` : input.routeName;
  context.fillText(routeTitle, padding, 225);
  context.font = "500 28px Arial";
  context.fillStyle = "rgba(255,255,255,.82)";
  context.fillText((input.date ?? new Date()).toLocaleDateString("es-EC", { day: "2-digit", month: "long", year: "numeric" }), padding, 270);

  if (input.background !== "map") drawRoute(context, input.track, 80, 310, width - 160, Math.max(230, panelTop - 350));

  roundedRectangle(context, 45, panelTop, width - 90, height - panelTop - 45, 38);
  context.fillStyle = "rgba(10, 28, 44, 0.84)";
  context.fill();

  const stats = [
    [input.stats.recordedDistanceKm.toFixed(2).replace(".", ","), "km"],
    [duration(input.stats.elapsedSeconds), "tiempo"],
    [`+${input.stats.ascentM}`, "m desnivel"],
    [input.stats.maximumAltitudeM === null ? "—" : String(input.stats.maximumAltitudeM), "m altitud máx."],
  ];
  const columns = 2;
  const cellWidth = (width - padding * 2) / columns;
  stats.forEach(([value, label], index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const left = padding + column * cellWidth;
    const top = panelTop + 100 + row * 155;
    context.fillStyle = "#ffffff";
    context.font = "800 54px Arial";
    context.fillText(value, left, top);
    context.fillStyle = "rgba(255,255,255,.7)";
    context.font = "600 24px Arial";
    context.fillText(label.toUpperCase(), left, top + 38);
  });

  context.fillStyle = "#ff9a38";
  context.font = "800 24px Arial";
  context.fillText("RUTAS Y AVENTURA · ECUADOR", padding, height - 85);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("No fue posible crear el PNG.")), "image/png");
  });
}
